import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  parseISO,
  setHours,
  setMinutes,
} from "date-fns";
import { pl } from "date-fns/locale";
import { RefreshCcw } from "lucide-react";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "../styles/AdminCalendar.css";
import AdminCalendarModal from "./AdminCalendarModal";

const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) =>
    format(date, formatStr, { ...options, locale: pl }),
  parse: (value, formatStr, backupDate, options) =>
    parse(value, formatStr, backupDate, { ...options, locale: pl }),
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1, locale: pl }),
  getDay,
  locales: { pl },
});

const messages = {
  today: "Dzisiaj",
  previous: "Poprzedni",
  next: "Następny",
  month: "Miesiąc",
  week: "Tydzień",
  day: "Dzień",
  agenda: "Agenda",
  date: "Data",
  time: "Godzina",
  event: "Zajęcia",
  allDay: "Cały dzień",
  noEventsInRange: "Brak wydarzeń w tym zakresie.",
};

const colorPalette = [
  "#3b82f6",
  "#ef4444",
  "#22c55e",
  "#f59e0b",
  "#8b5cf6",
  "#06b6d4",
  "#e11d48",
  "#84cc16",
];

const randomColor = () =>
  "#" + Math.floor(Math.random() * 16777215).toString(16);

// godziny dla widoku dzień/tydzień (07:00–21:00) + scroll do 07:00
const createTime = (hour, minute = 0) =>
  setHours(setMinutes(new Date(), minute), hour);

const dayMinTime = createTime(7, 0);
const dayMaxTime = createTime(21, 0);
const dayScrollTime = createTime(7, 0);

const CustomEvent = ({ event }) => {
  const ref = React.useRef(null);

  useEffect(() => {
    if (ref.current) {
      tippy(ref.current, {
        content: `
          <div>
            <strong>Moduł:</strong> ${event.module_name} <br/>
            <strong>Tytuł:</strong> ${event.session_title} <br/>
            <strong>Grupa:</strong> ${event.group_code} (${event.group_name}) <br/>
            <strong>Nauczyciel:</strong> ${
              event.lecturer || "Brak nauczyciela"
            } <br/>
            <strong>Sala:</strong> ${event.room_name || "Brak sali"} <br/>
            <strong>Data:</strong> ${format(event.start, "dd.MM.yyyy")} <br/>
            <strong>Godzina:</strong> ${format(
              event.start,
              "HH:mm"
            )} - ${format(event.end, "HH:mm")} <br/>
            <strong>Opis:</strong> ${
              event.session_description || "Brak opisu"
            }
          </div>
        `,
        allowHTML: true,
        theme: "light-border",
        placement: "top",
        maxWidth: 260,
        appendTo: () => document.body, // ważne, żeby tooltip nie był obcinany
      });
    }
  }, [event]);

  return <div ref={ref}>{event.module_name}</div>;
};

const ScheduleCalendar = forwardRef(({ selectedGroup, selectedLecturer }, ref) => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());

  // 🔹 ZAWSZE startujemy od "month"
  const [currentView, setCurrentView] = useState("month");

  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [moduleColors, setModuleColors] = useState(() => {
    const saved = localStorage.getItem("moduleColors");
    return saved ? JSON.parse(saved) : {};
  });

  const assignColors = React.useCallback(
    (sessions) => {
      setModuleColors((currentColors) => {
        let updatedColors = { ...currentColors };

        sessions.forEach((s) => {
          if (!updatedColors[s.module_name]) {
            const newColor =
              colorPalette[
                Object.keys(updatedColors).length % colorPalette.length
              ] || randomColor();
            updatedColors[s.module_name] = newColor;
          }
        });

        localStorage.setItem("moduleColors", JSON.stringify(updatedColors));
        return updatedColors;
      });
    },
    [] // No dependencies needed since we use functional update
  );

  const eventStyleGetter = (event) => {
    const backgroundColor = moduleColors[event.module_name] || "#6b7280";
    return {
      style: {
        backgroundColor,
        color: "white",
        borderRadius: "8px",
        border: "none",
        padding: "2px 4px",
      },
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const fetchSessions = React.useCallback(async () => {
    setLoading(true);
    try {
      // Build query params for filtering
      const params = new URLSearchParams();
      if (selectedGroup) {
        params.append('group_code', selectedGroup);
      }
      if (selectedLecturer) {
        params.append('lecturer_id', selectedLecturer);
      }
      
      const queryString = params.toString();
      const url = `/sessions${queryString ? '?' + queryString : ''}`;
      if (process.env.NODE_ENV === 'development') {
        console.log('[AdminCalendar] GET', url || '/sessions', {
          group: selectedGroup || '(wszystkie)',
          lecturer: selectedLecturer || '(wszyscy)',
        });
      }
      const res = await axios.get(url);
      if (!res.data || !Array.isArray(res.data)) {
        console.warn('Invalid response data:', res.data);
        setEvents([]);
        return;
      }
      const sessions = res.data
        .filter((session) => {
          // Filter out sessions with missing required data
          return session.session_date && session.start_time && session.end_time;
        })
        .map((session) => {
        // Handle date - PostgreSQL DATE returns as 'YYYY-MM-DD' string
        let date;
        if (typeof session.session_date === 'string') {
          // If it's already a string, parse it
          date = parseISO(session.session_date);
        } else if (session.session_date instanceof Date) {
          date = session.session_date;
        } else {
          // Fallback: try to parse as ISO or create new Date
          date = parseISO(session.session_date) || new Date(session.session_date);
        }
        
        // Validate date
        if (isNaN(date.getTime())) {
          console.warn('Invalid date for session:', session.session_id, session.session_date);
          return null;
        }
        
        // Handle time - PostgreSQL TIME returns as 'HH:MM:SS' or 'HH:MM:SS.mmm'
        const startTimeStr = typeof session.start_time === 'string' 
          ? session.start_time 
          : String(session.start_time || '00:00');
        const endTimeStr = typeof session.end_time === 'string' 
          ? session.end_time 
          : String(session.end_time || '00:00');
        
        const [startH, startM] = startTimeStr.split(":").map(Number);
        const [endH, endM] = endTimeStr.split(":").map(Number);
        const start = setMinutes(setHours(date, startH || 0), startM || 0);
        const end = setMinutes(setHours(date, endH || 0), endM || 0);

        const lecturer =
          session.lecturer_first_name || session.lecturer_last_name
            ? `${session.lecturer_academic_position ? session.lecturer_academic_position + "." : ""} ${
                session.lecturer_first_name || ""
              } ${session.lecturer_last_name || ""}`.trim()
            : "Brak nauczyciela";

        return {
          id: session.session_id,
          title: `${session.module_name} (${
            session.group_code?.trim() || ""
          }) – ${session.room_name || "Brak sali"}`,
          session_title: session.session_title || "Brak tytułu",
          start,
          end,
          module_name: session.module_name,
          group_code: session.group_code,
          group_name: session.group_name,
          room_name: session.room_name,
          session_description: session.session_description,
          lecturer,
        };
      })
      .filter(Boolean); // Remove any null entries

      setEvents(sessions);
      assignColors(sessions);
    } catch (err) {
      console.error("Błąd podczas pobierania zajęć:", err);
      console.error("Error details:", err.response?.data || err.message);
      // Set empty events on error so calendar still renders
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [assignColors, selectedGroup, selectedLecturer]);

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchSessions,
  }));

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // ❌ USUNIĘTY efekt zmieniający widok na resize – sterujemy ręcznie currentView

  if (loading) return <div>Ładowanie kalendarza...</div>;

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button onClick={fetchSessions} className="refresh-btn">
          <RefreshCcw size={14} />
          <span style={{ marginLeft: "5px" }}>Odśwież</span>
        </button>
      </div>

      <div className="calendar-scroll-wrapper">
        <Calendar
          onSelectEvent={handleSelectEvent}
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={currentView}
          date={currentDate}
          onView={(view) => setCurrentView(view)}
          onNavigate={(date) => setCurrentDate(date)}
          views={["month", "week", "day"]}
          messages={messages}
          style={{ height: "100%" }}
          components={{ event: CustomEvent }}
          eventPropGetter={eventStyleGetter}
          formats={{
            timeGutterFormat: "HH:mm",
            eventTimeRangeFormat: ({ start, end }) =>
              `${start.toLocaleTimeString("pl-PL", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })} - ${end.toLocaleTimeString("pl-PL", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}`,
            dayHeaderFormat: (date) =>
              date.toLocaleDateString("pl-PL", {
                weekday: "long",
                day: "2-digit",
                month: "long",
              }),
          }}
          // 👇 DODANE: zakres godzin i początek scrolla dla week/day
          min={dayMinTime}
          max={dayMaxTime}
          scrollToTime={dayScrollTime}
        />
      </div>

      {isModalOpen && (
        <AdminCalendarModal
          event={selectedEvent}
          onClose={() => setIsModalOpen(false)}
          onEdit={async (updatedEvent) => {
            try {
              await axios.put(
                `/sessions/update/${updatedEvent.id}`,
                {
                  start_time: format(updatedEvent.start, "HH:mm"),
                  end_time: format(updatedEvent.end, "HH:mm"),
                  session_date: format(updatedEvent.start, "yyyy-MM-dd"),
                  title: updatedEvent.session_title,
                  description: updatedEvent.session_description,
                  room_id: updatedEvent.room_id,
                  lecturer_id: updatedEvent.lecturer_id,
                }
              );
              setEvents(
                events.map((ev) => (ev.id === updatedEvent.id ? updatedEvent : ev))
              );
              setIsModalOpen(false);
            } catch (err) {
              console.error("Błąd przy edycji:", err);
            }
          }}
          onDelete={async (event) => {
            if (window.confirm("Na pewno chcesz usunąć to wydarzenie?")) {
              try {
                await axios.delete(
                  `/sessions/${event.id}`
                );
                setEvents(events.filter((e) => e.id !== event.id));
                setIsModalOpen(false);
              } catch (err) {
                console.error("Błąd przy usuwaniu:", err);
              }
            }
          }}
        />
      )}
    </div>
  );
});

ScheduleCalendar.displayName = 'ScheduleCalendar';

export default ScheduleCalendar;